import { useState } from 'react';
import AppLayout from '@/components/layout/AppLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { motion } from 'framer-motion';
import { Wifi, WifiOff, CloudOff, RefreshCw, User } from 'lucide-react';
import { profiloOperatore, statoConnessione } from '@/data/mockOperatoreData';
import HealthWorkerAgenda from '@/pages/HealthWorkerAgenda';

function ConnectionBanner({ connessione, onSync, syncing }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      data-testid="connection-banner"
      className={`
        rounded-2xl border px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4
        ${connessione.online
          ? 'bg-emerald-50/60 dark:bg-emerald-950/20 border-emerald-200/60 dark:border-emerald-800/40'
          : 'bg-orange-50/60 dark:bg-orange-950/20 border-orange-200/60 dark:border-orange-800/40'
        }
      `}
    >
      <div className="flex items-center gap-4">
        {/* Profile chip */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/15 flex items-center justify-center">
            <User className="w-5 h-5 text-primary" />
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground" data-testid="operatore-nome">
              {profiloOperatore.qualificaBreve} {profiloOperatore.nome} {profiloOperatore.cognome}
            </p>
            <p className="text-[11px] text-muted-foreground">
              {profiloOperatore.distretto} &middot; {profiloOperatore.turno}
            </p>
          </div>
        </div>

        {/* Separator */}
        <div className="hidden sm:block w-px h-8 bg-border/50" />

        {/* Connection Status */}
        <div className="flex items-center gap-2">
          {connessione.online ? (
            <>
              <div className="flex items-center gap-1.5">
                <Wifi className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              </div>
              <div>
                <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300" data-testid="connection-status">
                  Connesso - Dati Sincronizzati
                </p>
                <p className="text-[10px] text-emerald-600/70 dark:text-emerald-400/70">
                  Ultima sync: {connessione.ultimaSync} &middot; {connessione.paiSincronizzati}/{connessione.paiTotali} PAI
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-1.5">
                <CloudOff className="w-4 h-4 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-orange-700 dark:text-orange-300" data-testid="connection-status">
                  Modalita Offline - In attesa di segnale
                </p>
                <p className="text-[10px] text-orange-600/70 dark:text-orange-400/70">
                  I dati saranno sincronizzati quando la connessione sara disponibile
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Sync Button */}
      <Button
        variant="outline"
        className={`
          rounded-xl gap-2 font-semibold shrink-0 h-11 px-5
          ${connessione.online
            ? 'border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/30'
            : 'border-orange-300 dark:border-orange-700 text-orange-700 dark:text-orange-300 hover:bg-orange-100 dark:hover:bg-orange-900/30 opacity-60 cursor-not-allowed'
          }
        `}
        onClick={onSync}
        disabled={!connessione.online || syncing}
        data-testid="sync-pai-btn"
      >
        <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
        {syncing ? 'Sincronizzazione...' : 'Sync Mattutino PAI'}
      </Button>
    </motion.div>
  );
}

export default function OperatoreDashboard() {
  const [connessione, setConnessione] = useState(statoConnessione);
  const [syncing, setSyncing] = useState(false);

  const handleSync = () => {
    setSyncing(true);
    setTimeout(() => {
      setConnessione((prev) => ({
        ...prev,
        ultimaSync: new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }),
      }));
      setSyncing(false);
    }, 2000);
  };

  return (
    <AppLayout role="operatore">
      <div className="space-y-6" data-testid="operatore-dashboard">
        {/* Connection & Profile Banner */}
        <ConnectionBanner
          connessione={connessione}
          onSync={handleSync}
          syncing={syncing}
        />

        {/* Main Content: Agenda */}
        <HealthWorkerAgenda />
      </div>
    </AppLayout>
  );
}
